import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Scissors, ArrowLeft, Shirt, Plus, X, Loader2, Sparkles, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { authHeaders } from "@/utils/auth";
import { subscribeTopInset } from "@/utils/telegram";
import { EASE, btnGoldSolid, btnGold } from "@/components/accountUi";
import { pollJob } from "@/utils/jobs";
import { WARDROBE_ENABLED } from "@/utils/flags";
import { API } from "../utils/api";

const imgUrl = (id) => `${API}/images/${id}`;

const OCCASIONS = ["на работу", "на свидание", "на прогулку", "на вечер"];

// Фото вещи: жмём до 1024px — вешалке хватает, базе легко, ИИ видит детали
const normalizeClothImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 1024 / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * k);
      canvas.height = Math.round(img.naturalHeight * k);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = reject;
    img.src = url;
  });

const OutfitCard = ({ outfit, itemsById, testid }) => {
  const photos = (outfit.item_ids || []).map((id) => itemsById[id]).filter(Boolean);
  return (
    <div className="border border-white/10 p-5" data-testid={testid}>
      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((it) => (
            <img
              key={it._id}
              src={imgUrl(it.image_id)}
              alt={it.name}
              className="h-24 w-24 shrink-0 border border-white/10 object-cover"
              draggable={false}
            />
          ))}
        </div>
      )}
      <p className="mt-4 font-cormorant text-2xl font-light leading-snug text-[#F4F4F0]">
        {outfit.title}
      </p>
      {outfit.why && (
        <p className="mt-2 text-sm leading-relaxed text-[#A3A3A3]">{outfit.why}</p>
      )}
      {outfit.hairstyle && (
        <p
          className="mt-3 border-t border-white/5 pt-3 font-jetbrains text-[10px] uppercase leading-relaxed tracking-[0.15em] text-[#C2A878]"
          data-testid={`${testid}-hairstyle`}
        >
          Со стрижкой: {outfit.hairstyle}
        </p>
      )}
    </div>
  );
};

export default function WardrobePage() {
  const [authState, setAuthState] = useState("checking"); // checking | guest | ok
  const [items, setItems] = useState([]);
  const [tagging, setTagging] = useState(0);
  const [ootd, setOotd] = useState(null);
  const [occasion, setOccasion] = useState(OCCASIONS[0]);
  const [outfits, setOutfits] = useState(null);
  const [composing, setComposing] = useState(false);
  const [topInset, setTopInset] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => subscribeTopInset(setTopInset), []);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/wardrobe/items`, { headers: authHeaders() });
        if (stop) return;
        setItems(data.items || []);
        setAuthState("ok");
      } catch (e) {
        if (stop) return;
        if (e?.response?.status === 401) setAuthState("guest");
        else {
          setAuthState("ok");
          toast.error("Гардероб не загрузился. Обновите страницу.");
        }
      }
    })();
    return () => { stop = true; };
  }, []);

  // Образ дня: опрашиваем, пока стилист собирает
  useEffect(() => {
    if (authState !== "ok") return undefined;
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/wardrobe/outfit-of-day`, { headers: authHeaders() });
        if (cancelled) return;
        setOotd(data);
        if (data.status === "generating") timer = setTimeout(tick, 4000);
      } catch { /* образ дня — витрина, ошибку не показываем */ }
    };
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [authState]);

  const addItem = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let image;
    try {
      image = await normalizeClothImage(file);
    } catch {
      toast.error("Не удалось прочитать фото. Попробуйте другое.");
      return;
    }
    setTagging((n) => n + 1);
    try {
      const { data } = await axios.post(`${API}/wardrobe/items`, { image }, { headers: authHeaders() });
      const res = await pollJob(data.job_id, authHeaders());
      if (res?.item) {
        setItems((arr) => [res.item, ...arr]);
        toast.success(`В гардеробе: ${res.item.name}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не получилось распознать вещь. Попробуйте ещё раз.");
    } finally {
      setTagging((n) => Math.max(0, n - 1));
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Убрать «${item.name}» из гардероба?`)) return;
    try {
      await axios.delete(`${API}/wardrobe/items/${item._id}`, { headers: authHeaders() });
      setItems((arr) => arr.filter((x) => x._id !== item._id));
    } catch {
      toast.error("Не удалось удалить. Попробуйте ещё раз.");
    }
  };

  const compose = async () => {
    setComposing(true);
    setOutfits(null);
    try {
      const { data } = await axios.post(`${API}/wardrobe/outfits`, { occasion }, { headers: authHeaders() });
      const res = await pollJob(data.job_id, authHeaders());
      setOutfits(res.outfits || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Стилист не ответил. Попробуйте ещё раз.");
    } finally {
      setComposing(false);
    }
  };

  const itemsById = Object.fromEntries(items.map((it) => [it._id, it]));

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="wardrobe-page">
      <header
        className="fixed inset-x-0 top-0 z-40 border-b border-white/5 bg-[#0A0A0A]/60 backdrop-blur-xl"
        style={{ top: topInset }}
      >
        <div className="flex items-center justify-between px-6 py-4 md:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="wardrobe-logo">
            <Scissors size={15} strokeWidth={1.5} className="text-[#C2A878]" />
            <span className="font-jetbrains text-sm tracking-[0.35em] text-[#F4F4F0]">AFANASIEV</span>
          </Link>
          <Link
            to="/preview"
            className="flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:text-[#C2A878]"
            data-testid="wardrobe-back-link"
          >
            <ArrowLeft size={13} strokeWidth={1.5} />
            В приложение
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 pb-24" style={{ paddingTop: 128 + topInset }}>
        {!WARDROBE_ENABLED && (
          <div className="flex flex-col items-center pt-10 text-center" data-testid="wardrobe-wip">
            <Shirt size={40} strokeWidth={1} className="text-[#C2A878]" />
            <p className="mt-8 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
              ( Гардероб )
            </p>
            <h1 className="mt-4 max-w-md font-cormorant text-4xl font-light leading-[1.08] tracking-tight text-[#F4F4F0] md:text-5xl">
              Раздел в разработке
            </h1>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-[#A3A3A3]">
              Скоро здесь откроется ваш гардероб: фотографируйте вещи, а ИИ-стилист
              будет собирать из них образы под ваши пропорции лица и стрижки.
            </p>
            <Link
              to="/preview"
              className={`${btnGold} mt-10 px-12`}
              data-testid="wardrobe-wip-back"
            >
              В приложение
            </Link>
          </div>
        )}

        {WARDROBE_ENABLED && authState === "checking" && (
          <div className="flex justify-center pt-20" data-testid="wardrobe-loading">
            <Loader2 size={20} className="animate-spin text-[#C2A878]" />
          </div>
        )}

        {WARDROBE_ENABLED && authState === "guest" && (
          <div className="flex flex-col items-center pt-10 text-center" data-testid="wardrobe-login-gate">
            <Shirt size={40} strokeWidth={1} className="text-[#C2A878]" />
            <h1 className="mt-8 max-w-md font-cormorant text-4xl font-light leading-[1.08] tracking-tight text-[#F4F4F0]">
              Гардероб хранится в вашем кабинете
            </h1>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-[#A3A3A3]">
              Войдите — и фотографируйте вещи: ИИ сам их распознает, а стилист
              будет собирать из них образы под ваши пропорции и стрижки.
            </p>
            <Link
              to="/account"
              className={`${btnGoldSolid} mt-10 px-12`}
              data-testid="wardrobe-login-button"
            >
              <Lock size={14} strokeWidth={1.5} />
              Войти в кабинет
            </Link>
          </div>
        )}

        {WARDROBE_ENABLED && authState === "ok" && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <p className="font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                ( Гардероб )
              </p>
              <h1 className="mt-4 font-cormorant text-4xl font-light leading-[1.05] tracking-tight text-[#F4F4F0] md:text-5xl">
                Вещи знакомятся, стилист собирает
              </h1>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#A3A3A3]">
                Фотографируйте вещи — ИИ сам распознает тип, цвет и стиль.
                Образы собираются только из ваших вещей и согласуются с формой
                лица и рекомендованными стрижками.
              </p>
            </motion.div>

            {ootd?.status === "generating" && (
              <div
                className="mt-10 flex items-center gap-3 border border-white/10 p-5 text-sm text-[#A3A3A3]"
                data-testid="ootd-pending"
              >
                <Loader2 size={16} className="animate-spin text-[#C2A878]" />
                Стилист собирает ваш образ дня{ootd.occasion ? ` ${ootd.occasion}` : ""}…
              </div>
            )}
            {ootd?.status === "done" && ootd.outfit && (
              <div className="mt-10">
                <p className="mb-3 font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
                  ( Образ дня{ootd.occasion ? ` · ${ootd.occasion}` : ""} )
                </p>
                <OutfitCard outfit={ootd.outfit} itemsById={itemsById} testid="ootd-card" />
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={addItem}
              className="hidden"
              data-testid="wardrobe-add-input"
            />
            <button
              onClick={() => inputRef.current?.click()}
              className={`${btnGoldSolid} mt-10`}
              data-testid="wardrobe-add-button"
            >
              <Plus size={15} strokeWidth={1.5} />
              Добавить вещь
            </button>
            <p className="mt-3 text-center font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
              Крупно, на простом фоне — так ИИ распознает точнее
            </p>

            {tagging > 0 && (
              <div
                className="mt-6 flex items-center gap-3 border border-[#C2A878]/25 bg-[#C2A878]/[0.04] p-5 text-sm text-[#A3A3A3]"
                data-testid="wardrobe-tagging-card"
              >
                <Loader2 size={16} className="animate-spin text-[#C2A878]" />
                ИИ распознаёт вещь — обычно 10–20 секунд
              </div>
            )}

            {items.length === 0 && tagging === 0 ? (
              <p
                className="mt-12 border border-dashed border-white/10 p-8 text-center text-sm leading-relaxed text-[#525252]"
                data-testid="wardrobe-empty"
              >
                Гардероб пуст. Сфотографируйте первую вещь — дальше ИИ сделает сам.
              </p>
            ) : (
              <div className="mt-8 grid grid-cols-2 gap-3" data-testid="wardrobe-grid">
                {items.map((it, i) => (
                  <div key={it._id} className="group relative border border-white/10" data-testid={`wardrobe-item-${i}`}>
                    <img
                      src={imgUrl(it.image_id)}
                      alt={it.name}
                      className="aspect-square w-full object-cover"
                      draggable={false}
                    />
                    <button
                      onClick={() => removeItem(it)}
                      aria-label={`Удалить ${it.name}`}
                      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center bg-[#0A0A0A]/70 text-[#A3A3A3] backdrop-blur transition-colors duration-300 hover:text-[#C2A878]"
                      data-testid={`wardrobe-item-delete-${i}`}
                    >
                      <X size={13} strokeWidth={1.5} />
                    </button>
                    <div className="p-3">
                      <p className="font-cormorant text-lg font-light leading-tight text-[#F4F4F0]">{it.name}</p>
                      <p className="mt-1 font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252]">
                        {[it.category, it.color, it.style].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-14 border-t border-white/10 pt-10">
              <p className="font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
                ( Образы )
              </p>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="outfit-occasions">
                {OCCASIONS.map((o) => (
                  <button
                    key={o}
                    onClick={() => setOccasion(o)}
                    className={`border px-4 py-2 font-jetbrains text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                      occasion === o
                        ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                        : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                    }`}
                    data-testid={`outfit-occasion-${o.replace(/\s+/g, "-")}`}
                  >
                    {o}
                  </button>
                ))}
              </div>
              <button
                onClick={compose}
                disabled={composing || items.length < 2}
                className={`${btnGold} mt-5`}
                data-testid="outfits-compose-button"
              >
                {composing ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Стилист собирает образы…
                  </>
                ) : (
                  <>
                    <Sparkles size={15} strokeWidth={1.5} />
                    Собрать образы
                  </>
                )}
              </button>
              {items.length < 2 && (
                <p className="mt-3 text-center font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                  Добавьте хотя бы 2 вещи — из одной образ не собрать
                </p>
              )}
              {outfits && (
                <div className="mt-8 space-y-4" data-testid="outfits-result">
                  {outfits.map((o, i) => (
                    <OutfitCard key={i} outfit={o} itemsById={itemsById} testid={`outfit-card-${i}`} />
                  ))}
                  {outfits.length === 0 && (
                    <p className="border border-dashed border-white/10 p-6 text-center text-sm text-[#525252]">
                      Из этих вещей образ не собрался — добавьте ещё пару вещей.
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
